import { createSelector } from "reselect";
import Log from "../utils/Log";
import { calculateStripeTotal, getPlanDiscountInfo } from "../utils/saleUtils";
import currentSaleContext from "./currentSaleContext";
import currentSaleData from "./currentSaleData";
import { PlanInterval } from "../reducers/models";

function getTermCost(term:PlanInterval, monthlyCost:number|undefined, annualCost:number|undefined, weeklyCost: number | undefined):number {
  if (term === PlanInterval.Monthly) {
    if (monthlyCost === undefined) {
      const msg = `term is specified as ${term}, but no monthly option is associated with this plan (annual: ${annualCost} monthly: ${monthlyCost})`
      Log.error(msg)
      throw new Error(msg)
    }
    return monthlyCost
  }
  else if (term === PlanInterval.Yearly){
    if (annualCost === undefined) {
      const msg = `term is specified as ${term}, but no annual option is associated with this plan (annual: ${annualCost} monthly: ${monthlyCost})`
      Log.error(msg)
      throw new Error(msg)
    }
    return annualCost
  } 
  else if (term === PlanInterval.Weekly){
     if (weeklyCost === undefined) {
      const msg = `term is specified as ${term}, but no annual option is associated with this plan (annual: ${annualCost} monthly: ${monthlyCost}, weekly: ${weeklyCost})`
      Log.error(msg)
      throw new Error(msg)
    }
    return weeklyCost
  }
  else {
    const msg = `Unknown subscription billing term ${term}!`
    Log.error(msg)
    throw new Error(msg)
  }
}

export const getItems = (state: { cart: bb.state.ICart }) => state.cart.items;

export const getCostPerUser = createSelector(
  [currentSaleContext, getItems],
  (saleContext, cartItems):string|null => {
    if (!cartItems[0]) {
      return null;
    }
    let { item } = cartItems[0];

    if (!item) {
      return null;
    }

    if (saleContext.showSaleUI && saleContext.discountedCost) {
      item = saleContext.discountedCost[item.id];
    }

    let monthlyCost = item.monthlyCost;
    let annualCost = item.annualCost;
    let weeklyCost  = item.weeklyCost;

    return getTermCost(item.planInterval, monthlyCost, annualCost, weeklyCost).toFixed(2)
  }
);

//get regular day total
export const getPreDiscountTotal = createSelector([getItems], (cartItems):number|null => {
  if (!cartItems[0]) {
    return null;
  }
  let { seatQuantity, item } = cartItems[0];

  if (!seatQuantity || seatQuantity < 1) {
    seatQuantity = 1;
  }

  if (!item) {
    return null;
  }

  const itemSubtotal = getTermCost(item.planInterval, item.monthlyCost, item.annualCost, item.weeklyCost)

  return (itemSubtotal * seatQuantity);
});

//get discounted day total
export const getDiscountedTotal = createSelector(
  [currentSaleData, getItems],
  (saleData, cartItems):number|null => {
    if (!cartItems[0]) {
      return null;
    }

    let { seatQuantity, item } = cartItems[0];

    if (!seatQuantity || seatQuantity < 1) {
      seatQuantity = 1;
    }

    if (!saleData || !item) {
      //TODO: account for non-plan purchases
      return null;
    }

    if(!item.discountsAllowed) {
      return null;
    }

    const itemSubtotal = getTermCost(item.planInterval, item.monthlyCost, item.annualCost, item.weeklyCost)

    const planDiscountInfo = getPlanDiscountInfo(item, saleData)
    if (planDiscountInfo === null) {
      return null
    }

    const totalDayCost = calculateStripeTotal(
      itemSubtotal,
      seatQuantity,
      planDiscountInfo
    );

    if (totalDayCost === undefined) {
      return null
    }

    return totalDayCost;
  }
);
